/**
 * modelTools – explode, isolate, section and highlight the 3D models in a lesson.
 *
 * Lifted from `public/krpano/plugins/model_control.xml`, which was already plain
 * Three.js operating on materials and transforms, and shared so both players run
 * one implementation.
 *
 * Rules carried over from the krpano version, all worth keeping:
 *
 *   - Every transform derives from a CACHED home position, never from the
 *     current one. Deriving from current accumulates drift, so repeatedly
 *     nudging the explode slider would slowly tear a model apart permanently.
 *   - A single-mesh model is left completely alone. There is nothing to
 *     separate, and moving the only mesh just relocates the whole object.
 *   - Selection is shown by tinting, not by scaling. Scaling the chosen part
 *     destroys the authored placement scale, and makes a synced scale
 *     impossible to express.
 *
 * Two things this adds, both needed once a lesson can hold more than one asset:
 *
 *   - **A target.** The teacher selects one asset and explode/section act on
 *     that one alone; with nothing selected they act on everything, which is
 *     what a single-asset lesson wants. Parts are addressed by an id that
 *     includes the model, because mesh names are routinely blank or repeated
 *     across assets, and a name alone would isolate the wrong piece.
 *   - **Model-local geometry.** Every distance is measured in the model's OWN
 *     space rather than the world. Measuring in the world was wrong twice over:
 *     the numbers were captured before the layout system scaled and placed the
 *     asset, so they described a model that no longer existed, and the explode
 *     offset was then added to mesh positions expressed in the model's authored
 *     units. For anything not authored at metre scale that made explode
 *     invisible and put the section plane nowhere near the model.
 *
 * **Explode is tracked per model.** A student pulls one model apart on their
 * own copy while the teacher's explode drives another, so "how far apart is
 * this model" has to be answerable per asset (`explodeAmount`), and settable
 * for one asset without moving the selection the teacher's controls follow
 * (`explodeAsset`).
 *
 * **One material state machine.** Isolate, highlight and section all act on
 * the same `mesh.material` slot, and they used to do it independently, which
 * broke in two ways:
 *
 *   - `Material.clone()` copies `clippingPlanes` by cloning the PLANES. A part
 *     dimmed after a cut therefore carried a frozen copy of the plane, and when
 *     the model was carried away the cut stayed hanging in mid-air on it.
 *   - Section applied planes only to whatever material was current. Isolate,
 *     then section, then un-isolate put back originals that had never been
 *     given the plane, so the model came back whole with the cut still "on".
 *
 * Now every change goes through `refreshMaterials()`, which decides each part's
 * material from the whole state — highlighted -> its glowing copy; else dimmed
 * if isolated and not the kept part; else the original — and then gives
 * whichever one won the model's ONE live plane (the same reference, updated in
 * place by `update()`), or none.
 *
 * **Highlight never touches a shared material.** glTF files routinely share one
 * material across many meshes, so tinting the original would light up every
 * part that uses it. Each highlighted part gets its own clone, freed as soon as
 * the part stops glowing, and capped at `MAX_HIGHLIGHTED_PARTS` to bound what a
 * headset has to hold.
 *
 * The state this drives — `exploded`, `isolated`, `selected_part_id`, `clip`,
 * `highlighted_parts` — is carried by `TeacherContentState`, so a teacher's
 * manipulation reaches students through the session document.
 */

import * as THREE from 'three';

export type ClipAxis = 'x' | 'y' | 'z';

export interface ModelSummary {
  key: string;
  name: string;
  partCount: number;
}

export interface PickedPart {
  /** Which asset the part belongs to. */
  key: string;
  /** Stable id of the part, unique across the scene. */
  partId: string;
  /** Display name, which may be blank — never use it to address a part. */
  name: string;
  mesh: THREE.Mesh;
}

/** A part to make glow, as the teacher's published state names it. */
export interface HighlightRequest {
  partId: string;
  /** Any CSS colour three.js understands. Blank or missing uses the default. */
  color?: string | null;
}

export interface HighlightedPart {
  partId: string;
  /** The colour actually in use, the default filled in. */
  color: string;
}

/** Where a part is, for something that has to follow it (a label). */
export interface PartAnchor {
  mesh: THREE.Mesh;
  /**
   * Centre of the part's geometry in the MESH's own space. Carried through
   * `mesh.localToWorld` it lands on the part wherever explode or a grab has
   * moved it, which a world position captured once would not.
   */
  localCentre: THREE.Vector3;
}

/**
 * Most parts that can glow at once. Every highlight is a material clone, and
 * past a handful the class is no longer looking at anything in particular.
 */
export const MAX_HIGHLIGHTED_PARTS = 8;
export const DEFAULT_HIGHLIGHT_COLOR = '#ffc83d';

/**
 * Glow strength for lit materials. Emissive light is added regardless of the
 * room's lighting, so this reads under a dark skybox, while leaving enough of
 * the part's own shading that it still looks like the same object.
 */
const HIGHLIGHT_EMISSIVE_INTENSITY = 0.6;
/** How far an unlit (`MeshBasicMaterial`) part's colour moves toward the highlight. */
const HIGHLIGHT_TINT = 0.6;

type MaterialSlot = THREE.Material | THREE.Material[];

interface CachedPart {
  mesh: THREE.Mesh;
  /** `${modelKey}#${index}` — stable, and unique even when meshes are unnamed. */
  id: string;
  name: string;
  /** Pristine local position. Every explode is computed from here. */
  home: THREE.Vector3;
  /** Direction the part travels, in the MODEL's space: centre -> part centre. */
  direction: THREE.Vector3;
  /**
   * Converts a displacement in the model's space into the mesh's parent space.
   * Precomputed because the internal hierarchy never changes — only the root's
   * own transform does, and this is deliberately independent of that.
   */
  toParent: THREE.Matrix3;
  originalMaterial: MaterialSlot;
  dimMaterial: MaterialSlot | null;
  /** This part's own glowing copy, and the colour it was made in. */
  highlightMaterial: MaterialSlot | null;
  highlightColor: string | null;
}

interface CachedModel {
  key: string;
  name: string;
  root: THREE.Object3D;
  /** Centre of the model in its own space. */
  centre: THREE.Vector3;
  /** Largest dimension in the model's own space: explode distance and clip range. */
  span: number;
  parts: CachedPart[];
  /** Reused so an active cut can be re-derived without reallocating. */
  plane: THREE.Plane;
  /**
   * `[plane]`, allocated once. Every material on this model is handed this same
   * array, so moving the plane moves the cut on all of them — including copies
   * made after the cut began.
   */
  clipPlanes: THREE.Plane[];
  /** How far apart this model is, 0..1. Stays 0 for a model that cannot come apart. */
  amount: number;
}

export interface ModelTools {
  /**
   * Re-read the scene. Call after assets load or change.
   *
   * The previous cache is first put back exactly as it loaded, so an exploded
   * or dimmed part is never mistaken for its own pristine state. The tools'
   * state then carries over to models whose key survives — selection, explode
   * amounts, isolation, highlights and the cut — so a second asset arriving
   * does not undo what the teacher did to the first. A caller that re-derives
   * everything from a published source can `reset()` first for a clean slate.
   */
  collect(roots: THREE.Object3D[]): void;
  /** Every asset present, for a picker. */
  list(): ModelSummary[];
  /** Target one asset, or null for all of them. */
  select(key: string | null): void;
  selectedKey(): string | null;
  /** The key of the model `object` is, or belongs to; null if it is not one. */
  keyOf(object: THREE.Object3D | null | undefined): string | null;
  /** Separable meshes in the current target; below 2 there is nothing to explode. */
  partCount(): number;
  /** `t` is 0..1 — how far apart the parts travel. Acts on the target. */
  explode(t: number): void;
  /** Explode one asset only, leaving the selection where it is. */
  explodeAsset(key: string, t: number): void;
  /** How far apart one asset currently is, 0..1; 0 for an unknown key. */
  explodeAmount(key: string): number;
  /** Dim every part except `partId`; pass null or `on: false` to restore. */
  isolate(partId: string | null, on: boolean): void;
  /** One cross-section plane. `offset` is -1..1 across the model from its centre. */
  clip(axis: ClipAxis | null, offset: number, renderer: THREE.WebGLRenderer | null): void;
  /**
   * Make exactly these parts glow, replacing any previous set. Duplicates are
   * ignored and only the first `MAX_HIGHLIGHTED_PARTS` count. Ids for models
   * not loaded yet are kept, and light up when a `collect` brings them in.
   */
  highlight(parts: ReadonlyArray<HighlightRequest>): void;
  /** The parts currently asked to glow, in the order they were given. */
  highlighted(): HighlightedPart[];
  /** A part's mesh and geometric centre, for a label to follow; null if absent. */
  partAnchor(partId: string): PartAnchor | null;
  /** Keeps an active cut on the model when the model itself is moved. */
  update(): void;
  /** Everything back to how it loaded: explode, isolate, section and highlight. */
  reset(renderer: THREE.WebGLRenderer | null): void;
  /** The part under a ray, for picking in-world. */
  pick(raycaster: THREE.Raycaster): PickedPart | null;
  dispose(): void;
}

/**
 * Bounding box of `root` expressed in root's own space.
 *
 * `Box3.setFromObject` reports world coordinates, which for a model that has
 * been scaled and moved onto the dock describes where it currently sits rather
 * than how big it is. Everything here has to be independent of placement, so
 * the geometry is gathered through the root's inverse instead.
 */
function localBounds(root: THREE.Object3D): THREE.Box3 {
  const box = new THREE.Box3();
  const rootInverse = new THREE.Matrix4().copy(root.matrixWorld).invert();
  const toRoot = new THREE.Matrix4();

  root.traverse((child) => {
    const mesh = child as THREE.Mesh;
    if (!mesh.isMesh || !mesh.geometry) return;
    if (!mesh.geometry.boundingBox) mesh.geometry.computeBoundingBox();
    const geometryBox = mesh.geometry.boundingBox;
    if (!geometryBox) return;
    toRoot.multiplyMatrices(rootInverse, mesh.matrixWorld);
    box.union(geometryBox.clone().applyMatrix4(toRoot));
  });

  return box;
}

/** Centre of one mesh's geometry, in the root's space. */
function localCentreOf(mesh: THREE.Mesh, rootInverse: THREE.Matrix4): THREE.Vector3 {
  if (!mesh.geometry.boundingBox) mesh.geometry.computeBoundingBox();
  const geometryBox = mesh.geometry.boundingBox;
  if (!geometryBox) return new THREE.Vector3();
  const toRoot = new THREE.Matrix4().multiplyMatrices(rootInverse, mesh.matrixWorld);
  return geometryBox.clone().applyMatrix4(toRoot).getCenter(new THREE.Vector3());
}

function materialsOf(slot: MaterialSlot | null | undefined): THREE.Material[] {
  if (!slot) return [];
  return Array.isArray(slot) ? slot : [slot];
}

function cloneSlot(slot: MaterialSlot): MaterialSlot {
  return Array.isArray(slot) ? slot.map((m) => m.clone()) : slot.clone();
}

/**
 * Frees a COPY made here. Only the material itself: its textures are the
 * original's, shared by reference, and still in use by the original.
 */
function disposeSlot(slot: MaterialSlot | null): void {
  materialsOf(slot).forEach((m) => m.dispose());
}

function clampAmount(t: number): number {
  return Math.min(1, Math.max(0, Number.isFinite(t) ? t : 0));
}

/** A see-through copy, for the parts an isolate pushes into the background. */
function makeDimMaterial(source: MaterialSlot): MaterialSlot {
  const dim = cloneSlot(source);
  for (const material of materialsOf(dim)) {
    material.transparent = true;
    material.opacity = 0.15;
    // Dimmed parts must not occlude the isolated one behind them.
    material.depthWrite = false;
  }
  return dim;
}

/**
 * A glowing copy. Lit materials glow through `emissive`, which is added on top
 * of the lighting and so shows under any sky. Unlit ones (`MeshBasicMaterial`)
 * have no emissive term, so their colour is pulled toward the highlight.
 */
function makeHighlightMaterial(source: MaterialSlot, colour: THREE.Color): MaterialSlot {
  const glow = cloneSlot(source);
  for (const material of materialsOf(glow)) {
    const lit = material as THREE.Material & {
      emissive?: THREE.Color;
      emissiveIntensity?: number;
      emissiveMap?: THREE.Texture | null;
    };
    if (lit.emissive?.isColor) {
      lit.emissive.copy(colour);
      lit.emissiveIntensity = HIGHLIGHT_EMISSIVE_INTENSITY;
      // The map multiplies the colour: a mostly dark emissive map would swallow
      // the glow, leaving the highlighted part looking untouched.
      if (lit.emissiveMap) lit.emissiveMap = null;
      continue;
    }
    const unlit = material as THREE.Material & { color?: THREE.Color };
    if (unlit.color?.isColor) unlit.color.lerp(colour, HIGHLIGHT_TINT);
  }
  return glow;
}

/** Hand a material the model's live plane, or take the cut away. */
function setClipPlanes(material: THREE.Material, planes: THREE.Plane[] | null): void {
  // Compared by reference: `planes` is the model's one array, so this is only
  // true when the material already follows the live cut.
  if (material.clippingPlanes === planes) return;
  material.clippingPlanes = planes;
  material.clipShadows = planes !== null;
  material.needsUpdate = true;
}

export function createModelTools(): ModelTools {
  let models: CachedModel[] = [];
  let selected: string | null = null;
  let activeClip: { axis: ClipAxis; offset: number } | null = null;
  /**
   * The models the cut was made on, fixed when it was made. Deriving it from
   * the selection later would move a cut the teacher never moved, the moment
   * they picked a different asset to explode.
   */
  let clipKeys = new Set<string>();
  /** `partId: null` dims everything, which is what isolating nothing means. */
  let isolation: { partId: string | null } | null = null;
  /** partId -> colour, in the order given. */
  let highlights = new Map<string, string>();
  const displacement = new THREE.Vector3();

  /** Models the controls act on: the selection, or everything. */
  const targets = (): CachedModel[] => {
    if (!selected) return models;
    const model = models.find((m) => m.key === selected);
    return model ? [model] : models;
  };

  const findPart = (partId: string): CachedPart | null => {
    for (const model of models) {
      const part = model.parts.find((p) => p.id === partId);
      if (part) return part;
    }
    return null;
  };

  const explodeModel = (model: CachedModel, amount: number) => {
    // Nothing to come apart. Moving the only mesh would just relocate the model.
    if (model.parts.length < 2) {
      model.amount = 0;
      return;
    }
    model.amount = amount;
    // A full explode fills the room the layout left around this model and no
    // more, so it never reaches the next one (lessonLayout's explodeReach).
    const reachValue = model.root.userData?.explodeReach;
    const reach = typeof reachValue === 'number' && Number.isFinite(reachValue) ? Math.max(0, reachValue) : 1;
    for (const part of model.parts) {
      displacement
        .copy(part.direction)
        .multiplyScalar(amount * model.span * reach)
        .applyMatrix3(part.toParent);
      part.mesh.position.copy(part.home).add(displacement);
    }
  };

  /** Point the model's cut plane at where the model currently is in the world. */
  const refreshPlane = (model: CachedModel) => {
    if (!activeClip) return;
    const axis = activeClip.axis;
    const normal = new THREE.Vector3(
      axis === 'x' ? 1 : 0,
      axis === 'y' ? 1 : 0,
      axis === 'z' ? 1 : 0
    );
    // Measured from the model's own centre, so the slider means the same thing
    // whatever the model's size or where it sits in the room.
    const cutPoint = model.centre
      .clone()
      .addScaledVector(normal, activeClip.offset * model.span * 0.5)
      .applyMatrix4(model.root.matrixWorld);
    // Clipping planes are world-space, so the normal has to be rotated with the
    // model. Re-derived rather than cached: a grabbed model turns as it moves.
    const worldNormal = normal.clone().transformDirection(model.root.matrixWorld).normalize();
    model.plane.setFromNormalAndCoplanarPoint(worldNormal.negate(), cutPoint);
  };

  /** Which material one part should be wearing right now, making it if needed. */
  const materialFor = (part: CachedPart): MaterialSlot => {
    const colour = highlights.get(part.id);
    if (colour !== undefined) {
      if (part.highlightMaterial && part.highlightColor !== colour) {
        disposeSlot(part.highlightMaterial);
        part.highlightMaterial = null;
        part.highlightColor = null;
      }
      if (!part.highlightMaterial) {
        try {
          // An unrecognised colour leaves the default in place (three warns).
          const tint = new THREE.Color(DEFAULT_HIGHLIGHT_COLOR).setStyle(colour);
          part.highlightMaterial = makeHighlightMaterial(part.originalMaterial, tint);
          part.highlightColor = colour;
        } catch {
          part.highlightMaterial = null;
          part.highlightColor = null;
        }
      }
      // Beats isolate: a teacher who highlighted a part wants it seen, and a
      // highlighted part fading with the background would hide the point.
      if (part.highlightMaterial) return part.highlightMaterial;
    } else if (part.highlightMaterial) {
      // Freed as soon as the part stops glowing: the clones are exactly the
      // memory the cap exists to bound.
      disposeSlot(part.highlightMaterial);
      part.highlightMaterial = null;
      part.highlightColor = null;
    }

    if (isolation && part.id !== isolation.partId) {
      if (!part.dimMaterial) {
        try {
          part.dimMaterial = makeDimMaterial(part.originalMaterial);
        } catch {
          part.dimMaterial = null;
        }
      }
      if (part.dimMaterial) return part.dimMaterial;
    }

    return part.originalMaterial;
  };

  /** The one place a part's material is decided. See the header. */
  const refreshMaterials = () => {
    for (const model of models) {
      const planes = activeClip && clipKeys.has(model.key) ? model.clipPlanes : null;
      for (const part of model.parts) {
        const next = materialFor(part);
        if (part.mesh.material !== next) part.mesh.material = next;
        for (const material of materialsOf(next)) setClipPlanes(material, planes);
      }
    }
  };

  /**
   * Every cached part back exactly as it loaded — home position, original
   * material, uncut — and every copy made from it freed.
   */
  const restorePristine = () => {
    for (const model of models) {
      for (const part of model.parts) {
        if (model.amount > 0) part.mesh.position.copy(part.home);
        if (part.mesh.material !== part.originalMaterial) {
          part.mesh.material = part.originalMaterial;
        }
        for (const material of materialsOf(part.originalMaterial)) setClipPlanes(material, null);
        disposeSlot(part.dimMaterial);
        part.dimMaterial = null;
        disposeSlot(part.highlightMaterial);
        part.highlightMaterial = null;
        part.highlightColor = null;
      }
    }
  };

  const setClip = (axis: ClipAxis | null, offset: number, renderer: THREE.WebGLRenderer | null) => {
    const enabled = axis === 'x' || axis === 'y' || axis === 'z';
    activeClip = enabled
      ? { axis: axis as ClipAxis, offset: Number.isFinite(offset) ? offset : 0 }
      : null;
    // Local clipping is off by default and silently does nothing without this.
    if (renderer) renderer.localClippingEnabled = true;

    clipKeys = new Set(enabled ? targets().map((model) => model.key) : []);
    for (const model of models) {
      if (clipKeys.has(model.key)) refreshPlane(model);
    }
    refreshMaterials();
  };

  return {
    collect(roots) {
      // Measure nothing until the old cache is pristine again. Otherwise an
      // exploded part is cached with its exploded position as "home", and a
      // dimmed or glowing part with its copy as the "original" — which a later
      // restore would faithfully put back, and dispose would free while the
      // real material leaked.
      const amounts = new Map(models.map((model) => [model.key, model.amount]));
      restorePristine();
      const previous = selected;
      models = [];

      const usedKeys = new Set<string>();

      roots.forEach((root, rootIndex) => {
        if (!root) return;
        // Bounds are read off the matrices, so they have to be current.
        root.updateMatrixWorld(true);

        // Keys address a model in published state, so two models must never
        // share one. Names normally come from the asset id and are unique, but
        // the same asset linked twice would collide and silently make every
        // control act on both.
        let key = String(root.name || root.uuid || `asset_${rootIndex}`);
        if (usedKeys.has(key)) key = `${key}~${rootIndex}`;
        usedKeys.add(key);
        const box = localBounds(root);
        const centre = box.getCenter(new THREE.Vector3());
        const size = box.getSize(new THREE.Vector3());
        const span = Math.max(size.x, size.y, size.z) || 1;
        const rootInverse = new THREE.Matrix4().copy(root.matrixWorld).invert();

        const parts: CachedPart[] = [];
        root.traverse((child) => {
          const mesh = child as THREE.Mesh;
          if (!mesh.isMesh || !mesh.geometry) return;

          const direction = localCentreOf(mesh, rootInverse).sub(centre);
          // A part sitting exactly on the centre has no outward direction of its
          // own; send it up rather than leaving it behind when everything moves.
          if (direction.lengthSq() < 1e-8) direction.set(0, 1, 0);
          else direction.normalize();

          // Model space -> this mesh's parent space, for the explode offset.
          const parent = mesh.parent;
          const toParent = new THREE.Matrix3();
          if (parent) {
            const parentToRoot = new THREE.Matrix4().multiplyMatrices(
              rootInverse,
              parent.matrixWorld
            );
            toParent.setFromMatrix4(parentToRoot.invert());
          } else {
            toParent.identity();
          }

          parts.push({
            mesh,
            id: `${key}#${parts.length}`,
            name: String(mesh.name || ''),
            home: mesh.position.clone(),
            direction,
            toParent,
            originalMaterial: mesh.material,
            dimMaterial: null,
            highlightMaterial: null,
            highlightColor: null,
          });
        });

        const plane = new THREE.Plane();
        models.push({
          key,
          name: root.name || key,
          root,
          centre,
          span,
          parts,
          plane,
          clipPlanes: [plane],
          amount: 0,
        });
      });

      // Keep the teacher's choice across a reload; otherwise target the first
      // asset, so the toolbar always reports a real part count rather than the
      // sum across a scene the controls are not all acting on.
      selected =
        previous && models.some((m) => m.key === previous)
          ? previous
          : (models[0]?.key ?? null);

      // Put back what was being shown, now measured from true home positions.
      for (const model of models) {
        const amount = amounts.get(model.key) ?? 0;
        if (amount > 0) explodeModel(model, amount);
        if (activeClip && clipKeys.has(model.key)) refreshPlane(model);
      }
      refreshMaterials();
    },

    list() {
      return models.map((model) => ({
        key: model.key,
        name: model.name,
        partCount: model.parts.length,
      }));
    },

    select(key) {
      selected = key && models.some((m) => m.key === key) ? key : null;
    },

    selectedKey() {
      return selected;
    },

    keyOf(object) {
      // Walks up, so a hit on any mesh inside a model answers too.
      for (let node = object ?? null; node; node = node.parent) {
        const model = models.find((m) => m.root === node);
        if (model) return model.key;
      }
      return null;
    },

    partCount() {
      return targets().reduce((total, model) => total + model.parts.length, 0);
    },

    explode(t) {
      const amount = clampAmount(t);
      for (const model of targets()) explodeModel(model, amount);
    },

    explodeAsset(key, t) {
      const model = models.find((m) => m.key === key);
      if (model) explodeModel(model, clampAmount(t));
    },

    explodeAmount(key) {
      return models.find((m) => m.key === key)?.amount ?? 0;
    },

    isolate(partId, on) {
      // Dims across the whole scene, not just the target: isolating a part is a
      // teacher saying "look at this", and a second asset left at full strength
      // beside it defeats that.
      isolation = on ? { partId: partId ?? null } : null;
      refreshMaterials();
    },

    clip(axis, offset, renderer) {
      setClip(axis, offset, renderer);
    },

    highlight(parts) {
      const next = new Map<string, string>();
      for (const request of parts ?? []) {
        if (next.size >= MAX_HIGHLIGHTED_PARTS) break;
        const partId = typeof request?.partId === 'string' ? request.partId : '';
        if (!partId || next.has(partId)) continue;
        const colour = typeof request.color === 'string' ? request.color.trim() : '';
        next.set(partId, colour || DEFAULT_HIGHLIGHT_COLOR);
      }
      highlights = next;
      refreshMaterials();
    },

    highlighted() {
      return Array.from(highlights, ([partId, color]) => ({ partId, color }));
    },

    partAnchor(partId) {
      const part = findPart(partId);
      if (!part) return null;
      const geometry = part.mesh.geometry;
      if (!geometry.boundingBox) geometry.computeBoundingBox();
      const localCentre = geometry.boundingBox
        ? geometry.boundingBox.getCenter(new THREE.Vector3())
        : new THREE.Vector3();
      return { mesh: part.mesh, localCentre };
    },

    update() {
      // Costs nothing on the overwhelmingly common path of no active cut.
      if (!activeClip) return;
      for (const model of models) {
        if (clipKeys.has(model.key)) refreshPlane(model);
      }
    },

    reset(renderer) {
      // Every model, not just the target: a model exploded while it was
      // selected must not stay apart because the teacher has since picked
      // another one.
      for (const model of models) explodeModel(model, 0);
      isolation = null;
      highlights = new Map();
      setClip(null, 0, renderer);
    },

    /**
     * The part under a ray.
     *
     * This is what makes "label the part" possible: the student's answer is the
     * sub-mesh they picked, rather than a lettered option on a panel.
     */
    pick(raycaster) {
      for (const model of models) {
        const meshes = model.parts.map((part) => part.mesh);
        const hit = raycaster.intersectObjects(meshes, false)[0];
        if (!hit) continue;
        const mesh = hit.object as THREE.Mesh;
        const part = model.parts.find((p) => p.mesh === mesh);
        if (!part) continue;
        return { key: model.key, partId: part.id, name: part.name, mesh };
      }
      return null;
    },

    dispose() {
      // Originals back on the meshes before anyone frees the model, so the
      // real materials are what get disposed and no copy is left behind.
      restorePristine();
      models = [];
      selected = null;
      activeClip = null;
      clipKeys = new Set();
      isolation = null;
      highlights = new Map();
    },
  };
}
